import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Brain,
  Upload,
  FileText,
  Image,
  Table,
  Search,
  Trash2,
  Download,
  Eye,
  Plus,
  Folder,
  HardDrive,
  File,
  FileSpreadsheet,
  FileImage,
  Loader2,
  Tag,
  Calendar,
  User,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Categories avec leurs icônes et couleurs
const CATEGORIES = {
  administratif: { label: 'Administratif', icon: FileText, color: 'bg-blue-500' },
  comptable: { label: 'Comptable', icon: FileSpreadsheet, color: 'bg-green-500' },
  juridique: { label: 'Juridique', icon: File, color: 'bg-purple-500' },
  technique: { label: 'Technique', icon: HardDrive, color: 'bg-orange-500' },
  rh: { label: 'RH', icon: User, color: 'bg-pink-500' },
  commercial: { label: 'Commercial', icon: Folder, color: 'bg-amber-500' },
  autre: { label: 'Autre', icon: File, color: 'bg-gray-500' },
};

// File type icons
const getFileIcon = (type) => {
  switch(type) {
    case 'document': return FileText;
    case 'spreadsheet': return Table;
    case 'image': return Image;
    default: return File;
  }
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export default function BrainPage() {
  const { activeSociete } = useWorkspace();
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('tous');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    nom: '',
    description: '',
    categorie: 'autre',
    tags: '',
    file: null,
  });

  const token = localStorage.getItem('token');

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    if (!activeSociete?.id) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'tous') params.append('categorie', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await fetch(
        `${API_URL}/api/brain-memory/documents/${activeSociete.id}?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSociete?.id, selectedCategory, searchTerm, token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!activeSociete?.id) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/brain-memory/stats/${activeSociete.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  }, [activeSociete?.id, token]);

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [fetchDocuments, fetchStats]);

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.nom.trim()) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('societe_id', activeSociete.id);
      formData.append('nom', uploadForm.nom);
      formData.append('description', uploadForm.description || '');
      formData.append('categorie', uploadForm.categorie);
      formData.append('tags', uploadForm.tags);
      formData.append('file', uploadForm.file);
      
      const response = await fetch(`${API_URL}/api/brain-memory/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        toast.success('Document ajouté au cerveau !');
        setShowUploadDialog(false);
        setUploadForm({ nom: '', description: '', categorie: 'autre', tags: '', file: null });
        fetchDocuments();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async (docId) => {
    if (!confirm('Supprimer ce document ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/brain-memory/document/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success('Document supprimé');
        fetchDocuments();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (!activeSociete) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Sélectionnez une société pour accéder au cerveau</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Brain className="w-7 h-7 text-white" />
            </div>
            Mon Cerveau
          </h1>
          <p className="text-muted-foreground mt-1">
            Mémoire documentaire de {activeSociete.nom}
          </p>
        </div>
        
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600" data-testid="upload-doc-btn">
              <Upload className="w-4 h-4 mr-2" />
              Ajouter un document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                Ajouter au cerveau
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom du document *</Label>
                <Input
                  value={uploadForm.nom}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, nom: e.target.value }))}
                  placeholder="Ex: Kbis SCEA 2024"
                  data-testid="doc-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Description du document..."
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Catégorie</Label>
                  <Select
                    value={uploadForm.categorie}
                    onValueChange={(v) => setUploadForm(prev => ({ ...prev, categorie: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORIES).map(([key, cat]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <cat.icon className="w-4 h-4" />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Tags (séparés par ,)</Label>
                  <Input
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                    placeholder="kbis, 2024, siret"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Fichier *</Label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files[0] }))}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.png,.jpg,.jpeg,.gif,.webp"
                    data-testid="file-input"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    {uploadForm.file ? (
                      <div className="flex items-center justify-center gap-2">
                        <FileText className="w-8 h-8 text-primary" />
                        <div>
                          <p className="font-medium">{uploadForm.file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(uploadForm.file.size)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            setUploadForm(prev => ({ ...prev, file: null }));
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Cliquez pour sélectionner un fichier
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF, Word, Excel, Images, Texte
                        </p>
                      </>
                    )}
                  </label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={uploading || !uploadForm.file || !uploadForm.nom}
                className="bg-gradient-to-r from-purple-500 to-pink-500"
                data-testid="submit-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Ajouter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <FileText className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_documents}</p>
                  <p className="text-sm text-muted-foreground">Documents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <HardDrive className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_size_mb} MB</p>
                  <p className="text-sm text-muted-foreground">Stockage</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <FileSpreadsheet className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_category?.comptable?.count || 0}</p>
                  <p className="text-sm text-muted-foreground">Comptables</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                  <File className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_category?.administratif?.count || 0}</p>
                  <p className="text-sm text-muted-foreground">Administratifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un document..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-docs-input"
          />
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Catégorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Toutes les catégories</SelectItem>
            {Object.entries(CATEGORIES).map(([key, cat]) => (
              <SelectItem key={key} value={key}>
                <div className="flex items-center gap-2">
                  <cat.icon className="w-4 h-4" />
                  {cat.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Documents Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Brain className="w-16 h-16 text-muted-foreground/30 mb-4" />
            <p className="text-lg font-medium text-muted-foreground">
              Votre cerveau est vide
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Commencez par ajouter des documents
            </p>
            <Button onClick={() => setShowUploadDialog(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Ajouter un document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) => {
            const category = CATEGORIES[doc.categorie] || CATEGORIES.autre;
            const FileIcon = getFileIcon(doc.file_type);
            
            return (
              <Card key={doc.id} className="hover:shadow-md transition-shadow" data-testid={`doc-card-${doc.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={cn("p-2 rounded-lg", category.color, "bg-opacity-10")}>
                      <FileIcon className={cn("w-6 h-6", category.color.replace('bg-', 'text-'))} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium truncate" title={doc.nom}>
                        {doc.nom}
                      </h3>
                      {doc.description && (
                        <p className="text-sm text-muted-foreground truncate">
                          {doc.description}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          <category.icon className="w-3 h-3 mr-1" />
                          {category.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatFileSize(doc.file_size)}
                        </span>
                      </div>
                      
                      {doc.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {doc.tags.slice(0, 3).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              <Tag className="w-2.5 h-2.5 mr-1" />
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-1 mt-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                        >
                          <a href={`${API_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer">
                            <Eye className="w-4 h-4" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                        >
                          <a href={`${API_URL}${doc.file_url}`} download>
                            <Download className="w-4 h-4" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(doc.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
